import React, { useState, useEffect } from "react";
import { TextInput, SelectInput, FileInput, ArrayInput } from "react-admin";
import { IDropDownAppData } from "../../../common/redux/reducers/appDataReducer";
import "./DocumentUploader.css";
import { FormDataConsumer, required } from "react-admin";
import { useTheme } from "@material-ui/styles";
import { Theme, IconButton } from "@material-ui/core";
import { useSelector } from "react-redux";
import { AppState } from "../../../common/types";
import { useForm } from "react-final-form";
import { FormApi } from "final-form";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { AxiosInstance } from "axios";
import {
  DocumentApiFactory,
  Configuration,
  DocumentCreateOrUpdateDto,
} from "../../../common/data/service-proxies";
import { UploadedFileField, UploadStatus } from "./UploadedFileField";
import { ProLogsSimpleFormIterator } from "./ProLogsSimpleFormIterator";
import RemoveCircleRoundedIcon from "@material-ui/icons/RemoveCircleRounded";
import { DropEvent } from "react-dropzone";
import { GenericDateOrTimePicker } from "./GenericDateOrTimePicker";
import { DatePicker } from "@material-ui/pickers";

interface IDocumentUploaderProps {
  documentTypes: IDropDownAppData<number>[];
  source: string;
  label?: string;
  disabled?: boolean;
}

interface IDocumentInputProps {
  getSource?: (source: string) => string;
  scopedFormData?: DocumentCreateOrUpdateDto;
}

interface IHandleFileDropProps {
  form: FormApi<Record<string, any>, Partial<Record<string, any>>>;
  getSource: (source: string) => string;
  files: File[];
  event: DropEvent;
  axiosInstance: AxiosInstance | null;
  onUploadFailed?: (error: any) => void;
  onUploadSucceeded?: () => void;
  onUploadProgress?: (event: ProgressEvent) => void;
}

const handleDocumentDelete = async ({
  event,
  axiosInstance,
  form,
  getSource,
  url,
  name,
}: DocumentCreateOrUpdateDto &
  Pick<IHandleFileDropProps, "form" | "getSource" | "axiosInstance"> & {
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>;
  }) => {
  if (!axiosInstance) {
    console.error(
      "Trying to remove a file but the Axios instance was not supplied: ",
      {
        url,
        name,
      }
    );

    event.stopPropagation();
    return;
  }

  if (!url) {
    console.error("Trying to remove a file but the URL was not supplied: ", {
      url,
      name,
    });

    event.stopPropagation();
    return;
  }

  try {
    await DocumentApiFactory(
      undefined,
      axiosInstance.defaults.baseURL,
      axiosInstance
    ).apiAppDocumentDelete(url);
  } catch (e) {
    console.error("Server delete failed: ", e);
  } finally {
    form.change(getSource("file"), null);
    form.change(getSource("url"), null);
    form.change(getSource("fileName"), null);
  }
};

const handleFileDrop = async ({
  form,
  getSource,
  files,
  event,
  axiosInstance,
  onUploadFailed,
  onUploadSucceeded,
  onUploadProgress,
}: IHandleFileDropProps) => {
  if (!axiosInstance) {
    console.error("Dropped a file but the Axios instance was undefined: ", {
      files,
      event,
    });

    if (onUploadFailed) {
      onUploadFailed("Dropped a file but the Axios instance was undefined");
    }

    return;
  }

  const formData = new FormData();
  formData.append("file", files[0]);
  formData.append("fileName", files[0].name);
  const cfg: Configuration = new Configuration();
  cfg.baseOptions = {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: onUploadProgress,
  };
  try {
    const response = await DocumentApiFactory(
      cfg,
      axiosInstance.defaults.baseURL,
      axiosInstance
    ).apiAppDocumentUploadPost(files[0]);
    form.change(getSource("url"), response.data);
    form.change(getSource("fileName"), files[0].name);
    if (onUploadSucceeded) {
      onUploadSucceeded();
    }
  } catch (e) {
    console.error("Upload failed: ", e);
    if (onUploadFailed) {
      onUploadFailed(e);
    }
  }
};

export const DocumentUploader = (props: IDocumentUploaderProps) => {
  const { documentTypes, source, label, disabled, ...rest } = props;
  const theme: Theme = useTheme();
  const themeName = useSelector((x: AppState) => x.theme);
  return (
    <div className="document-uploader">
      <style>
        {`.document-uploader .document-input .file-input div[data-testid="dropzone"] {
          color: ${theme.palette.text.primary};
          background: repeating-linear-gradient(
            45deg, 
            ${
              themeName === "dark"
                ? theme.palette.grey[700]
                : theme.palette.grey[100]
            }, 
            ${
              themeName === "dark"
                ? theme.palette.grey[700]
                : theme.palette.grey[100]
            } 2%, 
            ${
              themeName === "dark"
                ? theme.palette.grey[600]
                : theme.palette.grey[200]
            } 2%, 
            ${
              themeName === "dark"
                ? theme.palette.grey[600]
                : theme.palette.grey[200]
            } 4%);
        }`}
      </style>
      <ArrayInput source={source} label={label ?? ""}>
        <ProLogsSimpleFormIterator
          disableAdd={disabled}
          disableRemove={disabled}>
          <FormDataConsumer>
            {(formDataProps) => {
              return (
                <>
                  {documentTypes && formDataProps.getSource && (
                    <SelectInput
                      {...rest}
                      label="resources.common.labels.documentName"
                      variant="outlined"
                      source={formDataProps.getSource("documentNameId")}
                      choices={[...documentTypes, { id: -1, name: "Custom" }]}
                      validate={required()}
                      disabled={disabled}
                    />
                  )}

                  <DocumentInput {...formDataProps} {...rest} />
                </>
              );
            }}
          </FormDataConsumer>
        </ProLogsSimpleFormIterator>
      </ArrayInput>
    </div>
  );
};

const DocumentInput = (formDataProps: IDocumentInputProps) => {
  const { scopedFormData, getSource, ...rest } = formDataProps;
  const form = useForm();
  const axiosInstance = useAxiosInstance();
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("none");

  useEffect(() => {
    if (!getSource || !scopedFormData?.id) {
      return;
    }

    form.change(getSource("id"), scopedFormData.id);
  }, [form, getSource, scopedFormData]);

  return (
    <div className="document-input">
      {scopedFormData?.documentNameId === -1 && getSource && (
        <TextInput
          source={getSource("name")}
          label="resources.common.labels.customDocumentName"
          variant="outlined"
        />
      )}
      {scopedFormData?.documentNameId && getSource && (
        <GenericDateOrTimePicker
          variant="outlined"
          PickerComponent={DatePicker}
          source={getSource("expirationDate")}
          label="resources.common.labels.expirationDate"
          options={{
            autoOk: true,
            format: "MMMM do yyyy",
          }}
        />
      )}
      <>
        {scopedFormData?.documentNameId &&
          getSource &&
          !scopedFormData?.fileName && (
            <div className="file-input">
              <FileInput
                {...rest}
                source={getSource("file")}
                label=""
                accept="application/pdf"
                options={{
                  disabled: uploadStatus === "uploading",
                  onDropAccepted: (files: File[], event: DropEvent) => {
                    setUploadStatus("uploading");
                    handleFileDrop({
                      form,
                      getSource,
                      files,
                      event,
                      axiosInstance,
                      onUploadFailed: () => {
                        setUploadStatus("failed");
                      },
                      onUploadSucceeded: () => {
                        setUploadStatus("uploaded");
                      },
                    });
                  },
                }}>
                <UploadedFileField
                  uploadStatus={uploadStatus}
                  downloadLink={scopedFormData.url}
                  title={scopedFormData.fileName}
                  source="src"
                />
              </FileInput>
            </div>
          )}
        {scopedFormData?.fileName && getSource && (
          <div style={{ marginTop: "-8px" }}>
            <IconButton
              onClick={(e) => {
                handleDocumentDelete({
                  event: e,
                  getSource,
                  axiosInstance,
                  form,
                  ...scopedFormData,
                });
                setUploadStatus("none");
              }}>
              <RemoveCircleRoundedIcon color="error" />
            </IconButton>
            <UploadedFileField
              uploadStatus="uploaded"
              downloadLink={scopedFormData.url}
              title={scopedFormData.fileName}
              source="src"
            />
          </div>
        )}
      </>
    </div>
  );
};
